import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const MASCOT_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";

export default function Login() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  useEffect(() => {
    const qrEmail = searchParams.get("email");
    const qrPassword = searchParams.get("password");
    if (qrEmail) setEmail(qrEmail);
    if (qrPassword) setPassword(qrPassword);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.first_name}!`);
      navigate(user.onboarding_complete ? from : "/onboarding", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <img src={MASCOT_IMAGE} alt="Bitcoin Brain" className="w-10 h-10 rounded-lg" />
            <span className="font-heading text-xl font-bold text-white">
              BITCOIN <span className="text-[#F7931A]">BRAIN</span>
            </span>
          </Link>

          <h1 className="font-heading text-4xl font-bold text-white mb-2">
            WELCOME <span className="text-[#F7931A]">BACK</span>
          </h1>
          <p className="text-zinc-400 mb-8">
            Continue your Bitcoin education journey
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-black/50 border-white/10 text-white h-12"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 bg-black/50 border-white/10 text-white h-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary h-12 text-lg"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  Logging in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Login <ArrowRight size={18} />
                </span>
              )}
            </Button>
          </form>

          <p className="text-zinc-500 text-center mt-8">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#F7931A] hover:underline" data-testid="signup-link">
              Sign up free
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:flex flex-1 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${MASCOT_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] to-transparent" />
        <div className="absolute inset-0 bg-[#050505]/50" />
        <div className="relative z-10 flex items-end p-12">
          <div className="max-w-md">
            <p className="text-[#F7931A] font-heading text-2xl font-bold">
              "Earn Bitcoin Experience while you learn."
            </p>
            <p className="text-zinc-400 mt-2">
              Every lesson completed, every quiz passed, every game finished — you earn BTC XP.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
